import React, { useContext } from 'react';
import { WidgetPanelDetailProps } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanel';
import JsonPanel from 'utils/components/JsonPanel/JsonPanel';
import { MyPortfolioCapsulesWidgetConfigState } from 'widgets/myPortfolioCapsules/store/config/MyPortfolioCapsulesWidgetConfigState';
import { selectMyPortfolioCapsulesDataState } from 'widgets/myPortfolioCapsules/store/data/selectors';
import {
    AggregationKey,
    MyPortfolioCapsulesDataState,
    MyPortfolioCapsulesSymbolDataState,
} from 'widgets/myPortfolioCapsules/store/data/MyPortfolioCapsulesDataState';
import { WidgetPanelGrid } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelGrid';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { NumberFormat } from '@instinet-analytics/core-lib';
import AppContext from 'app/AppContext';
import { computeMyPortfolioCapsulesData } from 'widgets/myPortfolioCapsules/store/data/MyPortfolioCapsulesData';

export interface WidgetPanelMyPortfolioCapsulesProps
    extends WidgetPanelDetailProps {
    aggregationKey: AggregationKey;
    showJson?: boolean;
    values?: boolean;
}

export default function WidgetPanelMyPortfolioCapsules(
    props: WidgetPanelMyPortfolioCapsulesProps
) {
    const {
        aggregationKey,
        state,
        widgetState,
        height,
        width,
        showJson,
        values,
    } = props;
    const { numberFormat } = useContext(AppContext);

    const config = widgetState.config as MyPortfolioCapsulesWidgetConfigState;
    const aggregatedData = selectMyPortfolioCapsulesDataState(state.state);
    const appSettings = state?.state?.app?.appSettings;
    const currency = appSettings?.currencyCode
        ? (state?.state?.app?.currencies ?? {})[appSettings.currencyCode]
        : undefined;
    const exchangeRate = currency ? currency.rate : 1;

    if (values) {
        const widgetValues = computeMyPortfolioCapsulesData(
            aggregatedData,
            aggregationKey,
            config,
            exchangeRate,
            true
        );

        const style = { width, height };

        const colDefs: ColDef[] = [
            {
                field: 'symbol',
                width: 80,
                resizable: true,
            },
            ...Object.keys(widgetValues[0])
                .filter((k) => k !== 'symbol')
                .map((k) => toNumberColDef(numberFormat, k, 2)),
        ];

        return (
            <WidgetPanelGrid
                data={widgetValues}
                getRowDataId={(d) => d.symbol}
                csvFileName={'myPortfolioCapsulesValues'}
                style={style}
                colDefs={colDefs}
            />
        );
    }

    return (
        <div className="widget-panel portfolio-summary-widget-panel">
            {aggregatedData && !showJson && (
                <AggregatedData
                    aggregatedData={aggregatedData}
                    aggregationKey={aggregationKey}
                    width={width - 20}
                    height={height - 115}
                />
            )}
            {aggregatedData && showJson && (
                <JsonPanel data={aggregatedData} key={'mps-ag'} />
            )}
        </div>
    );
}

export function AggregatedData(props: {
    aggregatedData: MyPortfolioCapsulesDataState;
    aggregationKey: AggregationKey;
    width: number;
    height: number;
}) {
    const { aggregationKey, aggregatedData, width, height } = props;
    const style = { width, height };
    const { numberFormat } = useContext(AppContext);

    const { data, colDefs } = flattenData(
        aggregatedData,
        aggregationKey,
        numberFormat
    );
    return (
        <WidgetPanelGrid
            data={data}
            getRowDataId={(d) => d.symbol}
            csvFileName={'myPortfolioCapsulesAggregatedData'}
            style={style}
            colDefs={colDefs}
        />
    );
}

export function addMyPortfolioCapsulesPanes(
    detailProps: WidgetPanelDetailProps,
    panes: { menuItem: string; render: () => JSX.Element }[]
) {
    const render = () => (
        <WidgetPanelMyPortfolioCapsules
            {...detailProps}
            aggregationKey={'allValid'}
        />
    );
    panes.push({
        menuItem: 'Aggregated Data',
        render: render,
    });

    const renderJson = () => (
        <WidgetPanelMyPortfolioCapsules
            {...detailProps}
            showJson={true}
            aggregationKey={'allValid'}
        />
    );
    panes.push({
        menuItem: 'Aggregated Data Json',
        render: renderJson,
    });

    const renderValues = () => (
        <WidgetPanelMyPortfolioCapsules
            {...detailProps}
            values={true}
            aggregationKey={'allValid'}
        />
    );
    panes.push({
        menuItem: 'Values',
        render: renderValues,
    });

    panes.push({
        menuItem: 'Aggregated Data - excluding done',
        render: () => (
            <WidgetPanelMyPortfolioCapsules
                {...detailProps}
                aggregationKey={'excludingDone'}
            />
        ),
    });

    panes.push({
        menuItem: 'Values - excluding done',
        render: () => (
            <WidgetPanelMyPortfolioCapsules
                {...detailProps}
                values={true}
                aggregationKey={'excludingDone'}
            />
        ),
    });

    panes.push({
        menuItem: 'Aggregated Data - only done',
        render: () => (
            <WidgetPanelMyPortfolioCapsules
                {...detailProps}
                aggregationKey={'onlyDone'}
            />
        ),
    });

    panes.push({
        menuItem: 'Values - only done',
        render: () => (
            <WidgetPanelMyPortfolioCapsules
                {...detailProps}
                values={true}
                aggregationKey={'excludingDone'}
            />
        ),
    });
}

function flattenData(
    aggregatedData: MyPortfolioCapsulesDataState,
    aggregationKey: AggregationKey,
    nf: NumberFormat
) {
    const flatten = (
        symbol: string,
        item: MyPortfolioCapsulesSymbolDataState
    ) => ({
        symbol,
        count: item.count,
        ...item.accumulated,
        ...item.derived,
        ...item.weights,
    });

    const data = [
        {
            symbol: 'TOTALS',
            count: aggregatedData[aggregationKey].totals.count,
            ...aggregatedData[aggregationKey].totals.accumulated,
        },
        ...Object.keys(aggregatedData[aggregationKey].bySymbol).map((k) =>
            flatten(k, aggregatedData[aggregationKey].bySymbol[k])
        ),
    ];

    const colDefs: ColDef[] = [
        {
            field: 'symbol',
            width: 80,
            resizable: true,
        },
        toNumberColDef(nf, 'count', 0),
        ...Object.keys(aggregatedData[aggregationKey].totals.accumulated).map(
            (k) => toNumberColDef(nf, k, 2)
        ),
        ...Object.keys(aggregatedData[aggregationKey].totals.weights).map((k) =>
            toNumberColDef(nf, k, 2)
        ),
    ];

    const bySymbolKeys = Object.keys(aggregatedData[aggregationKey].bySymbol);

    if (bySymbolKeys.length > 0) {
        colDefs.push(
            ...Object.keys(
                aggregatedData[aggregationKey].bySymbol[bySymbolKeys[0]].derived
            )
                .filter((f) => !colDefs.find((c) => c.field === f))
                .map((k) => toNumberColDef(nf, k, 2))
        );
        colDefs.push(
            ...Object.keys(
                aggregatedData[aggregationKey].bySymbol[bySymbolKeys[0]].weights
            )
                .filter((f) => !colDefs.find((c) => c.field === f))
                .map((k) => toNumberColDef(nf, k, 2))
        );
    }

    return { data, colDefs };
}

function toNumberColDef(nf: NumberFormat, field: string, dps: number) {
    return {
        field,
        width: 100,
        resizable: true,
        sortable: true,
        cellRenderer: (props: { value?: number }) => {
            return (
                <span>
                    {props.value === undefined ? '' : nf.n(dps, props.value)}
                </span>
            );
        },
        cellStyle: { textAlign: 'right' },
    };
}
